import { fromCents, toCents } from '@lib/money'
import { PostPayUpType } from '@orbit/api-types'

/**
 * The PayUp Form core: the Draft the Add PayUp modal holds, its entry states,
 * its step transitions, its validation and the create payload. Pure functions
 * only — no React, no DOM.
 *
 * PayUps are create-and-delete only, so there is no update payload.
 */

/** Amounts above this read as a slip rather than a payment, and earn a nudge. */
const SOFT_WARN_THRESHOLD_CENTS = 100_000_00

/** The balance row a PayUp can be started from: who owes whom, and how much. */
export interface PayUpPrefill {
	fromUserId: number
	toUserId: number
	amount: number
	currencyCode: string
}

/** 1 = who paid, 2 = who was paid, 3 = the details. */
export type PayUpStep = 1 | 2 | 3

/** A PayUp being recorded. The amount stays a decimal string exactly as typed. */
export interface PayUpDraft {
	step: PayUpStep
	/**
	 * The step the flow opened on. Steps before it were never walked — a
	 * two-person trip skips the payer step, a prefilled flow skips both — so
	 * the back button, the breadcrumb and the step count all key off it.
	 */
	firstStep: PayUpStep
	fromUserId: number | null
	toUserId: number | null
	amount: string
	currencyCode: string
}

export const emptyPayUpDraft = (currencyCode: string): PayUpDraft => ({
	step: 1,
	firstStep: 1,
	fromUserId: null,
	toUserId: null,
	amount: '',
	currencyCode,
})

/**
 * Opened from a balance row: both people and the amount owed are already
 * settled, so the flow starts on the details step. A zero balance leaves the
 * amount blank rather than prefilling a figure the user must clear.
 */
export const payUpDraftFromPrefill = (prefill: PayUpPrefill): PayUpDraft => ({
	step: 3,
	firstStep: 3,
	fromUserId: prefill.fromUserId,
	toUserId: prefill.toUserId,
	amount: prefill.amount > 0 ? fromCents(prefill.amount, prefill.currencyCode) : '',
	currencyCode: prefill.currencyCode,
})

export interface OpeningPayUpDraftOptions {
	memberIds: number[]
	currentUserId: number
	defaultCurrencyCode: string
	prefill?: PayUpPrefill
}

/**
 * The state the modal opens in. A two-person trip has only one possible pair,
 * so the payer step would be a formality — it opens on the recipient step with
 * the current user already the payer.
 */
export const openingPayUpDraft = ({
	memberIds,
	currentUserId,
	defaultCurrencyCode,
	prefill,
}: OpeningPayUpDraftOptions): PayUpDraft => {
	if (prefill) return payUpDraftFromPrefill(prefill)
	if (memberIds.length !== 2) return emptyPayUpDraft(defaultCurrencyCode)
	return {
		...emptyPayUpDraft(defaultCurrencyCode),
		step: 2,
		firstStep: 2,
		fromUserId: currentUserId,
		toUserId: memberIds.find(id => id !== currentUserId) ?? null,
	}
}

// MARK: Transitions

/** Picks who paid, and moves on. Nobody can pay themselves, so a clash clears the recipient. */
export const selectPayer = (draft: PayUpDraft, userId: number): PayUpDraft => ({
	...draft,
	step: 2,
	fromUserId: userId,
	toUserId: draft.toUserId === userId ? null : draft.toUserId,
})

export const selectRecipient = (draft: PayUpDraft, userId: number): PayUpDraft => ({
	...draft,
	step: 3,
	toUserId: userId,
})

/** Jumps to a step the breadcrumb offers. Choices already made are kept. */
export const goToStep = (draft: PayUpDraft, step: PayUpStep): PayUpDraft => ({ ...draft, step })

export const setAmount = (draft: PayUpDraft, amount: string): PayUpDraft => ({ ...draft, amount })

export const setCurrencyCode = (draft: PayUpDraft, currencyCode: string): PayUpDraft => ({
	...draft,
	currencyCode,
})

/** Is there a step behind this one? Only the steps the flow actually walked through count. */
export const canGoBack = (draft: PayUpDraft): boolean => draft.step > draft.firstStep

/** One step back, never past the step the flow opened on. */
export const stepBack = (draft: PayUpDraft): PayUpDraft =>
	canGoBack(draft) ? { ...draft, step: (draft.step - 1) as PayUpStep } : draft

/** The breadcrumb only makes sense when every step is there to jump between. */
export const showsBreadcrumb = (draft: PayUpDraft): boolean => draft.firstStep === 1

/**
 * "Step 2 of 3", counting only the steps the user actually walks through: a
 * two-person trip opens on the recipient step, so that is its "Step 1 of 2".
 * A prefilled flow has a single step and nothing to count, so it gets null.
 */
export const stepIndicatorLabel = (draft: PayUpDraft): string | null => {
	const total = 4 - draft.firstStep
	if (total <= 1) return null
	return `Step ${Math.max(draft.step - draft.firstStep + 1, 1)} of ${total}`
}

// MARK: Validation

export interface PayUpValidation {
	/** The blocking problem, or null when the PayUp is ready to record. */
	error: string | null
	canSubmit: boolean
	/** A nudge, not a block: the amount is large enough to look like a typo. */
	showsHighAmountWarning: boolean
}

export const validatePayUpDraft = (draft: PayUpDraft): PayUpValidation => {
	const amountCents = toCents(draft.amount)
	const error = (() => {
		if (amountCents <= 0) return 'Amount must be greater than zero'
		if (draft.fromUserId == null || draft.toUserId == null)
			return 'Payer and recipient are required'
		if (draft.fromUserId === draft.toUserId) return 'Payer and recipient must be different'
		return null
	})()
	return {
		error,
		canSubmit: error === null,
		showsHighAmountWarning: amountCents > SOFT_WARN_THRESHOLD_CENTS,
	}
}

// MARK: Payload

/**
 * The create request, or null when the draft is not recordable — the one gate
 * the submit handler needs, so an invalid draft can never reach the wire.
 */
export const toCreatePayload = (draft: PayUpDraft): PostPayUpType | null => {
	if (!validatePayUpDraft(draft).canSubmit || draft.fromUserId == null || draft.toUserId == null) {
		return null
	}
	return {
		fromUserId: draft.fromUserId,
		toUserId: draft.toUserId,
		amount: toCents(draft.amount),
		currencyCode: draft.currencyCode,
	}
}
